import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Rocket, ShieldCheck, Eye, Layers } from 'lucide-react';
import './About.css';

export const About = () => {
  const principles = [
    { icon: Rocket, title: 'Built for production', text: 'We’ve seen too many “AI projects” that never ship. We build for production from day one.' },
    { icon: ShieldCheck, title: 'We don’t over-promise', text: 'We scope carefully, build reliably, and support long-term. No vague magic.' },
    { icon: Layers, title: 'Modular by design', text: 'Components that work independently or compose together — so systems grow with you.' },
    { icon: Eye, title: 'Observable by default', text: 'Clear visibility into performance, cost, and failures. You always know what your agent is doing.' },
  ];

  const values = [
    'Outcomes over demos',
    'Systems thinking over hacks',
    'Long-term reliability over short-term wins',
  ];

  return (
    <div className="about-page">
      {/* Hero */}
      <section className="about-hero">
        <div className="about-hero-glow" aria-hidden="true"></div>
        <h1 className="about-hero-title fade-in">
          We build AI agents that work in the real world.
        </h1>
        <p className="about-hero-text fade-in" style={{ animationDelay: '0.15s' }}>
          Liceron is a product-first AI company. We build production-grade agents — not demos — for
          businesses that need intelligence at scale.
        </p>
        <p className="about-hero-subtext fade-in" style={{ animationDelay: '0.25s' }}>
          Conversational bots, workflow automations, research agents, and full enterprise AI systems —
          scoped, built, delivered, and supported by one team.
        </p>
      </section>

      {/* Philosophy */}
      <section className="about-philosophy">
        <div className="philosophy-container">
          <h2 className="section-title fade-in">Our philosophy</h2>
          <p className="philosophy-lead fade-in" style={{ animationDelay: '0.1s' }}>
            Every business workflow that can be automated, should be — not with fragile scripts, but
            with intelligent, observable, production-grade agents.
          </p>
          <p className="philosophy-text fade-in" style={{ animationDelay: '0.2s' }}>
            Intelligence isn’t about complexity. It’s about clarity, structure, and reliable execution
            inside the systems a business already runs.
          </p>
        </div>
      </section>

      {/* What makes us different */}
      <section className="about-build">
        <div className="build-container">
          <h2 className="section-title fade-in">What makes us different</h2>
          <div className="build-list fade-in" style={{ animationDelay: '0.2s' }}>
            {principles.map((b, i) => {
              const Icon = b.icon;
              return (
                <div key={i} className="build-item">
                  <div className="build-item-icon"><Icon size={18} /></div>
                  <div>
                    <h3 className="build-item-title">{b.title}</h3>
                    <p className="build-item-text">{b.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="about-team">
        <div className="team-container">
          <h2 className="section-title fade-in">The team</h2>
          <p className="team-lead fade-in" style={{ animationDelay: '0.1s' }}>
            We’re engineers and designers who believe in shipping — not just demoing.
          </p>
          <div className="values-list fade-in" style={{ animationDelay: '0.2s' }}>
            {values.map((v, i) => (
              <div key={i} className="value-item">{v}</div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="about-cta">
        <div className="cta-container fade-in">
          <h2 className="cta-title">Want to work with us?</h2>
          <p className="cta-text">Tell us what you need. We’ll scope it in 24 hours.</p>
          <Link to="/contact" className="cta-button">
            Book a Scoping Call <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
};
